import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/layout/Navbar';
import { api } from '@/utils/api';
import { useAuth } from '@/context/AuthContext';
import { VipAssinaturaPlanosBlock } from '@/components/perfil/VipAssinaturaPlanosBlock';
import { PaymentModal } from '@/components/perfil/PaymentModal';
import { PaymentsHistoryModal } from '@/components/perfil/PaymentsHistoryModal';
import { PixDiscordPaymentNotice } from '@/components/payment/PixDiscordPaymentNotice';

export default function PlanosPage() {
    const navigate = useNavigate();
    const { isAuthenticated, isLoading: authLoading, refreshTokenAndUpdateUser } = useAuth();

    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState<any>(null);

    const [vipPlans, setVipPlans] = useState<any[]>([]);
    const [plansCatalogLoading, setPlansCatalogLoading] = useState(false);

    const [showPaymentsModal, setShowPaymentsModal] = useState(false);
    const [myPayments, setMyPayments] = useState<any[]>([]);
    const [paymentsLoading, setPaymentsLoading] = useState(false);
    const [checkingPaymentId, setCheckingPaymentId] = useState<number | null>(null);

    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [paymentLoading, setPaymentLoading] = useState(false);
    const [paymentData, setPaymentData] = useState<any>(null);
    const [paymentError, setPaymentError] = useState('');
    const [toast, setToast] = useState<{ type: 'error' | 'success'; text: string } | null>(null);

    useEffect(() => {
        if (authLoading) return;
        if (!isAuthenticated) {
            navigate(`/entrar?next=${encodeURIComponent('/planos')}`, { replace: true });
            return;
        }

        let cancelled = false;
        (async () => {
            try {
                setLoading(true);
                const res: any = await api.get('usuarios/me');
                if (!cancelled) setUser(res.data);
            } catch {
                if (!cancelled) setUser(null);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [isAuthenticated, authLoading, navigate]);

    useEffect(() => {
        if (!isAuthenticated || authLoading) return;

        const loadPlans = async () => {
            try {
                setPlansCatalogLoading(true);
                const response = await api.get('planos');
                const body = (response as any)?.data ?? response;
                const list = Array.isArray(body?.data) ? body.data : Array.isArray(body) ? body : [];
                setVipPlans(list);
            } catch {
                setVipPlans([]);
            } finally {
                setPlansCatalogLoading(false);
            }
        };

        loadPlans();
    }, [isAuthenticated, authLoading]);

    const fetchMyPayments = async () => {
        try {
            setPaymentsLoading(true);
            const response = await api.get('meus-pagamentos');
            const res: any = (response as any)?.data ?? response;
            setMyPayments(Array.isArray(res.items) ? res.items : Array.isArray(res) ? res : []);
        } catch (e) {
            console.error('Erro ao carregar pagamentos:', e);
        } finally {
            setPaymentsLoading(false);
        }
    };

    const handleCheckPaymentStatus = async (paymentId: number) => {
        setCheckingPaymentId(paymentId);
        try {
            const response = await api.post(`meus-pagamentos/${paymentId}/verificar`, {});
            const res: any = (response as any)?.data ?? response;
            if (res?.status === 'ativado' || res?.status === 'ja_ativado') {
                setMyPayments((prev) => prev.map((p) => (p.id === paymentId ? { ...p, status: 'completed' } : p)));
                setToast({ type: 'success', text: 'Pagamento confirmado! VIP ativado.' });
                await refreshTokenAndUpdateUser();
                const fresh: any = await api.get('usuarios/me');
                setUser(fresh.data);
            } else {
                setToast({ type: 'error', text: 'Pagamento ainda não confirmado.' });
            }
        } catch (e) {
            console.error(e);
            setToast({ type: 'error', text: 'Erro ao verificar o estado do pagamento.' });
        } finally {
            setCheckingPaymentId(null);
        }
    };

    useEffect(() => {
        if (!toast) return;
        const t = setTimeout(() => setToast(null), 4000);
        return () => clearTimeout(t);
    }, [toast]);

    const createPaymentLink = async (plan: any) => {
        try {
            setPaymentLoading(true);
            setPaymentError('');

            const referrerNick = typeof window !== 'undefined' ? localStorage.getItem('referrerNick') : null;
            const body: Record<string, string> = {};
            if (referrerNick) body.referrerNick = referrerNick;

            const response = await api.post(`planos/${plan.id}/comprar`, body);
            const data: any = (response as any)?.data ?? response;

            if (data.checkoutUrl) {
                setPaymentData({ checkoutUrl: data.checkoutUrl, reference: data.reference, plan });
                setShowPaymentModal(true);
                if (referrerNick && typeof window !== 'undefined') {
                    localStorage.removeItem('referrerNick');
                }
            } else {
                throw new Error('Resposta sem link de pagamento');
            }
        } catch (err: any) {
            console.error(err);
            const msg = err?.message || 'Erro ao gerar link de pagamento. Tenta de novo.';
            setPaymentError(msg);
            setToast({ type: 'error', text: msg });
        } finally {
            setPaymentLoading(false);
        }
    };

    const closePaymentModal = () => {
        setShowPaymentModal(false);
        setPaymentData(null);
        setPaymentError('');
    };

    const proceedToPayment = () => {
        if (paymentData?.checkoutUrl) {
            window.open(paymentData.checkoutUrl, '_blank');
            closePaymentModal();
        }
    };

    if (authLoading || (loading && !user)) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-neutral-950">
                <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-t-2 border-orange-500" />
            </div>
        );
    }

    if (!user) {
        return null;
    }

    return (
        <div className="relative min-h-screen overflow-x-hidden bg-neutral-950 pb-24">
            <div className="pointer-events-none absolute inset-0 -z-0">
                <div className="absolute -left-24 -top-24 h-64 w-64 rounded-full bg-orange-500/15 blur-3xl" />
                <div className="absolute -right-24 top-1/3 h-72 w-72 rounded-full bg-orange-500/10 blur-3xl" />
            </div>
            <Navbar />

            <main className="relative z-10 mx-auto max-w-6xl px-4 pb-8 pt-6 md:px-8 lg:pt-28">
                {toast && (
                    <div
                        className={`mb-4 rounded-lg border px-4 py-3 text-sm ${
                            toast.type === 'success'
                                ? 'border-green-500/30 bg-green-500/10 text-green-200'
                                : 'border-red-500/30 bg-red-500/10 text-red-200'
                        }`}
                    >
                        {toast.text}
                    </div>
                )}

                <PixDiscordPaymentNotice className="mb-6 max-w-2xl" />

                {plansCatalogLoading && vipPlans.length === 0 ? (
                    <div className="flex justify-center py-16">
                        <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-t-2 border-orange-500" />
                    </div>
                ) : (
                    <VipAssinaturaPlanosBlock
                        user={user}
                        vipPlans={vipPlans}
                        plansLoading={paymentLoading}
                        onSelectPlan={createPaymentLink}
                        showPaymentsHistory={() => {
                            setShowPaymentsModal(true);
                            void fetchMyPayments();
                        }}
                    />
                )}
            </main>

            <PaymentModal
                show={showPaymentModal}
                onClose={closePaymentModal}
                paymentData={paymentData}
                paymentError={paymentError}
                onProceed={proceedToPayment}
            />

            <PaymentsHistoryModal
                show={showPaymentsModal}
                onClose={() => setShowPaymentsModal(false)}
                payments={myPayments}
                loading={paymentsLoading}
                checkingId={checkingPaymentId}
                onCheckStatus={handleCheckPaymentStatus}
                donations={[]}
                donationsLoading={false}
            />
        </div>
    );
}
